import type { DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { faFileImport, faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer, MailrailBaseEmailCampaign } from '@feathr/blackbox';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  EmptyState,
  Fieldset,
  Text,
  toast,
  Tooltip,
} from '@feathr/components';
import ConfirmButton from '@feathr/extender/components/ConfirmButton';
import type { ISegmentSelectSimpleChangeProps } from '@feathr/extender/components/SegmentSelectSimple';
import { useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';

import type { TSegmentContainerId } from './DraggableSegmentSelect';
import DraggableSegmentSelect from './DraggableSegmentSelect';

import * as styles from './MailrailCampaignSegments.css';

const PLACEHOLDER_INCLUDED = 'placeholder-included';
const PLACEHOLDER_EXCLUDED = 'placeholder-excluded';

function toSortableId(segmentId: string, containerId: TSegmentContainerId): string {
  return segmentId || (containerId === 'included' ? PLACEHOLDER_INCLUDED : PLACEHOLDER_EXCLUDED);
}

function fromSortableId(sortableId: string): string {
  return [PLACEHOLDER_INCLUDED, PLACEHOLDER_EXCLUDED].includes(sortableId) ? '' : sortableId;
}

function getOverContainer(event: DragOverEvent): TSegmentContainerId | undefined {
  const overData = event.over?.data.current as
    | { containerId?: TSegmentContainerId; sortable?: { containerId: TSegmentContainerId } }
    | undefined;
  return overData?.sortable?.containerId ?? overData?.containerId;
}

interface IDroppableFieldsetProps {
  children: React.ReactNode;
  className?: string;
  containerId: TSegmentContainerId;
  label: string;
}

function DroppableFieldset({
  children,
  className,
  containerId,
  label,
}: Readonly<IDroppableFieldsetProps>): JSX.Element {
  const { setNodeRef } = useDroppable({ id: containerId, data: { containerId } });

  return (
    <Fieldset className={className} label={label}>
      <div ref={setNodeRef}>{children}</div>
    </Fieldset>
  );
}

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  showImportContacts?: boolean;
  step?: number;
  title?: string;
  disabledTooltip?: string;
}

function MailrailCampaignSegments({
  campaign,
  disabled,
  showImportContacts = true,
  step,
  title,
  disabledTooltip,
}: Readonly<IProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const user = useUser();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { Goals, Importers, Segments, Targetings } = useStore();
  const importerRef = useRef<Importer | undefined>();
  const [uniqueEmailsCount, setUniqueEmailsCount] = useState(0);

  const committedIncludedIds = campaign.get('segments_included', []);
  const committedExcludedIds = campaign.get('segments_excluded', []);
  const realCommittedIncludedIds = campaign.get('segments_included', []).filter(Boolean);
  const realCommittedExcludedIds = campaign.get('segments_excluded', []).filter(Boolean);
  const allRealSegmentIds = [...realCommittedIncludedIds, ...realCommittedExcludedIds];

  // During drag, local state tracks visual positions using sortable IDs (not segment IDs)
  // so that placeholders retain their unique identity when moved between containers
  const [localIncludedSortableIds, setLocalIncludedSortableIds] = useState<string[] | null>(null);
  const [localExcludedSortableIds, setLocalExcludedSortableIds] = useState<string[] | null>(null);

  const includedIdsToRender = localIncludedSortableIds?.map(fromSortableId) ?? committedIncludedIds;
  const excludedIdsToRender = localExcludedSortableIds?.map(fromSortableId) ?? committedExcludedIds;

  useEffect(() => {
    async function getUniqueEmails(): Promise<void> {
      try {
        const response = await Segments.getUniqueEmailsFromSegments({
          included_segment_ids: realCommittedIncludedIds,
          excluded_segment_ids: realCommittedExcludedIds,
        });
        if (response) {
          setUniqueEmailsCount(response.unique_emails);
        }
      } catch (error) {
        // Set the unique emails count to 0 instead of showing the error
        setUniqueEmailsCount(0);
      }
    }

    realCommittedIncludedIds.length > 0 ? getUniqueEmails() : setUniqueEmailsCount(0);
  }, [realCommittedIncludedIds.join(','), realCommittedExcludedIds.join(',')]);

  useEffect(() => {
    if (committedIncludedIds.filter(Boolean).length === 0) {
      campaign.set({ target_address_type: 'primary' });
    }
  }, [committedIncludedIds.filter(Boolean).length]);

  function setIncludedSegmentIds(newIds: string[]): void {
    campaign.set({ segments_included: newIds });
    if (!newIds.length) {
      campaign.set({ consent: { has_consent: false } });
    }
  }

  function setExcludedSegmentIds(newIds: string[]): void {
    campaign.set({ segments_excluded: newIds });
  }

  async function addImporter(): Promise<string | void> {
    importerRef.current = undefined;
    try {
      const importerAttrs: Record<string, string> = {
        created_by: user.id,
        kind: 'person',
        state: 'draft',
        // TODO: Translate Importer name.
        name: `Import ${moment().format(TimeFormat.isoDate)}`,
      };

      const newImporter = Importers.create(importerAttrs);
      const response = await Importers.add(newImporter, { validate: false });

      importerRef.current = response;
      return response.get('segment');
      // Campaign is saved by <ConfirmButton />.
    } catch (e) {
      toast(t('There was an error creating the import.'), { type: 'error' });
    }
  }

  async function addIncludedImporter(): Promise<void> {
    const newSegmentId = await addImporter();
    if (newSegmentId) {
      setIncludedSegmentIds([...committedIncludedIds, newSegmentId]);
    }
  }

  function getRedirect(): string | false {
    if (!importerRef.current) {
      return false;
    }
    return localUrl(importerRef.current.getItemUrl());
  }

  function changeSegment(oldId: string | undefined, id: string, excluded: boolean): void {
    const segmentIds = excluded ? committedExcludedIds : committedIncludedIds;
    const oldIdIndex = oldId === undefined ? -1 : segmentIds.indexOf(oldId);
    if (oldIdIndex === -1) {
      // This should not happen.
      return;
    }

    const newIds = [...segmentIds];
    newIds[oldIdIndex] = id;
    if (excluded) {
      setExcludedSegmentIds(newIds);
    } else {
      setIncludedSegmentIds(newIds);
    }
  }

  function handleChangeIncludedSegment({ oldId, id }: ISegmentSelectSimpleChangeProps): void {
    changeSegment(oldId, id, false);
  }

  function handleChangeExcludedSegment({ oldId, id }: ISegmentSelectSimpleChangeProps): void {
    changeSegment(oldId, id, true);
  }

  function handleRemoveIncludedSegment(id?: string): void {
    if (id) {
      setIncludedSegmentIds(committedIncludedIds.filter((s) => s !== id));
    } else {
      setIncludedSegmentIds(committedIncludedIds.filter((s) => s !== ''));
    }
  }

  function handleRemoveExcludedSegment(id?: string): void {
    if (id) {
      setExcludedSegmentIds(committedExcludedIds.filter((s) => s !== id));
    } else {
      setExcludedSegmentIds(committedExcludedIds.filter((s) => s !== ''));
    }
  }

  function addIncludedSegment(): void {
    setIncludedSegmentIds([...committedIncludedIds, '']);
  }

  function addExcludedSegment(): void {
    setExcludedSegmentIds([...committedExcludedIds, '']);
  }

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  function handleDragStart(event: DragStartEvent): void {
    setActiveDragId(event.active.id as string);
    setLocalIncludedSortableIds(committedIncludedIds.map((id) => toSortableId(id, 'included')));
    setLocalExcludedSortableIds(committedExcludedIds.map((id) => toSortableId(id, 'excluded')));
  }

  function handleDragOver(event: DragOverEvent): void {
    const { active, over } = event;
    if (!over || !localIncludedSortableIds || !localExcludedSortableIds) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    const overContainer = getOverContainer(event);

    if (!overContainer) {
      return;
    }

    const activeContainer: TSegmentContainerId = localIncludedSortableIds.includes(activeId)
      ? 'included'
      : 'excluded';

    if (activeContainer === overContainer) {
      // The segment is just being moved to a different position within the same container
      const ids =
        activeContainer === 'included'
          ? [...localIncludedSortableIds]
          : [...localExcludedSortableIds];
      const fromIndex = ids.indexOf(activeId);
      const toIndex = ids.indexOf(overId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }
      ids.splice(fromIndex, 1);
      ids.splice(toIndex, 0, activeId);
      if (activeContainer === 'included') {
        setLocalIncludedSortableIds(ids);
      } else {
        setLocalExcludedSortableIds(ids);
      }
    } else {
      // The segment is being moved from included to excluded or vice-versa
      const sourceIds =
        activeContainer === 'included'
          ? [...localIncludedSortableIds]
          : [...localExcludedSortableIds];
      const destIds =
        overContainer === 'included'
          ? [...localIncludedSortableIds]
          : [...localExcludedSortableIds];
      const sourceIndex = sourceIds.indexOf(activeId);

      if (sourceIndex === -1) {
        return;
      }

      // Prevent moving a placeholder into a container that already has one
      if (!fromSortableId(activeId) && destIds.some((id) => !fromSortableId(id))) {
        return;
      }

      sourceIds.splice(sourceIndex, 1);
      const destIndex = destIds.indexOf(overId);
      if (destIndex >= 0) {
        destIds.splice(destIndex, 0, activeId);
      } else {
        destIds.push(activeId);
      }

      if (activeContainer === 'included') {
        setLocalIncludedSortableIds(sourceIds);
        setLocalExcludedSortableIds(destIds);
      } else {
        setLocalExcludedSortableIds(sourceIds);
        setLocalIncludedSortableIds(destIds);
      }
    }
  }

  function handleDragEnd(): void {
    if (localIncludedSortableIds && localExcludedSortableIds) {
      setIncludedSegmentIds(localIncludedSortableIds.map(fromSortableId));
      setExcludedSegmentIds(localExcludedSortableIds.map(fromSortableId));
    }
    setActiveDragId(null);
    setLocalIncludedSortableIds(null);
    setLocalExcludedSortableIds(null);
  }

  function handleDragCancel(): void {
    setActiveDragId(null);
    setLocalIncludedSortableIds(null);
    setLocalExcludedSortableIds(null);
  }

  return (
    <CardV2 width={'full'}>
      <CardHeader title={title ?? t('Groups')}>
        {(campaign.get('segments_included').filter(Boolean).length ||
          !campaign.isMailrailAutoSend) && (
          <Text.DefaultMedium mantineComponent={'span'}>
            {t('Total Unique Contacts: {{count}} ', { count: uniqueEmailsCount })}
            <Tooltip title={t('The number of unique emails between all selected groups.')}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </Text.DefaultMedium>
        )}
      </CardHeader>
      <CardContent addVerticalGap={true}>
        <DndContext
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragStart={handleDragStart}
          sensors={sensors}
        >
          <SortableContext
            id={'included'}
            items={
              localIncludedSortableIds ??
              committedIncludedIds.map((id) => toSortableId(id, 'included'))
            }
            strategy={verticalListSortingStrategy}
          >
            <DroppableFieldset
              className={styles.segment}
              containerId={'included'}
              label={t('Included Groups')}
            >
              {!includedIdsToRender.length && (
                <EmptyState label={t('No included groups added')} theme={'neutral'} />
              )}
              {includedIdsToRender.map((segmentId) => {
                const sortableId = toSortableId(segmentId, 'included');
                return (
                  <Observer key={sortableId}>
                    {(): JSX.Element => {
                      const affectedTargetings = Targetings.list({
                        filters: {
                          _parent__ne: campaign.id,
                          _target_data: segmentId || undefined,
                        },
                        pagination: {
                          distinct: 'parent',
                        },
                      });
                      const affectedGoals = Goals.list({
                        filters: { segment: segmentId || undefined },
                      });
                      return (
                        <DraggableSegmentSelect
                          affectedCampaigns={affectedTargetings.pagination.distinct}
                          affectedGoals={affectedGoals.pagination.count}
                          containerId={'included'}
                          disabled={disabled}
                          excludeIds={allRealSegmentIds}
                          id={sortableId}
                          isBeingDragged={activeDragId === sortableId}
                          onChange={handleChangeIncludedSegment}
                          onRemove={handleRemoveIncludedSegment}
                          value={segmentId}
                        />
                      );
                    }}
                  </Observer>
                );
              })}
            </DroppableFieldset>
          </SortableContext>
          <SortableContext
            id={'excluded'}
            items={
              localExcludedSortableIds ??
              committedExcludedIds.map((id) => toSortableId(id, 'excluded'))
            }
            strategy={verticalListSortingStrategy}
          >
            <DroppableFieldset
              className={styles.segment}
              containerId={'excluded'}
              label={t('Excluded Groups')}
            >
              {!excludedIdsToRender.length && (
                <EmptyState label={t('No excluded groups added')} theme={'neutral'} />
              )}
              {excludedIdsToRender.map((segmentId) => {
                const sortableId = toSortableId(segmentId, 'excluded');
                return (
                  <Observer key={sortableId}>
                    {(): JSX.Element => {
                      const affectedTargetings = Targetings.list({
                        filters: {
                          _parent__ne: campaign.id,
                          _target_data: segmentId || undefined,
                        },
                        pagination: {
                          distinct: 'parent',
                        },
                      });
                      const affectedGoals = Goals.list({
                        filters: { segment: segmentId || undefined },
                      });
                      return (
                        <DraggableSegmentSelect
                          affectedCampaigns={affectedTargetings.pagination.distinct}
                          affectedGoals={affectedGoals.pagination.count}
                          containerId={'excluded'}
                          disabled={disabled}
                          excludeIds={allRealSegmentIds}
                          id={sortableId}
                          isBeingDragged={activeDragId === sortableId}
                          onChange={handleChangeExcludedSegment}
                          onRemove={handleRemoveExcludedSegment}
                          value={segmentId}
                        />
                      );
                    }}
                  </Observer>
                );
              })}
            </DroppableFieldset>
          </SortableContext>
        </DndContext>
      </CardContent>
      <CardActions contentClassName={styles.cardActions}>
        <Flex gap={theme.spacing['2']}>
          <Button
            disabled={disabled || committedIncludedIds.includes('')}
            name={'add_included_segment'}
            onClick={addIncludedSegment}
            prefix={<FontAwesomeIcon icon={faPlus} />}
            tooltip={disabledTooltip}
          >
            {t('Add included group')}
          </Button>
          <Button
            disabled={disabled || committedExcludedIds.includes('')}
            name={'add_excluded_segment'}
            onClick={addExcludedSegment}
            prefix={<FontAwesomeIcon icon={faPlus} />}
            tooltip={disabledTooltip}
          >
            {t('Add excluded group')}
          </Button>
        </Flex>
        {showImportContacts && (
          <ConfirmButton
            description={t(
              'Before you can import contacts, the changes to your campaign need to be saved.',
            )}
            disabled={disabled}
            model={campaign}
            name={'import_contacts'}
            onConfirm={addIncludedImporter}
            prefix={<FontAwesomeIcon icon={faFileImport} />}
            redirect={getRedirect}
            step={step}
          >
            {t('Import contacts')}
          </ConfirmButton>
        )}
      </CardActions>
    </CardV2>
  );
}

export default observer(MailrailCampaignSegments);
